import { ICellRendererProps } from '../../../../common/components/table/table.types';
import { Tag } from 'antd';
import React from 'react';
import { ITrafficAdmittanceType, ITrafficRead } from '../../../../common/services/traffic-service/traffic-service.types';
import { entryTypeColorMap, entryTypeMap } from '../../constans/common.eiq-secure';
import { admittanceColorMap, admittanceTagClassMap, admittanceTypeMap } from 'common/constans/admittance.constants';
import { visitorTypeMap, visitorTypeMapColorMap } from 'common/constans/visitor.constants';
import { EntryType } from '../../../../common/enums/entry-type.enum';

import { AdmittanceType } from '../../../../common/enums/admittance-type.enum';
import BaseCellText from '../../../../common/components/table/base-cell-text';
import { formatCustomDateTime } from '../../../../common/helpers/date.helper';
import {
  ownershipTypeStatusColorMap,
  trafficLogOwnershipTypeStatusMap,
  TrafficLogVisitorType,
} from '../../../../common/constans/common.constants';
import { ReactComponent as Expired } from 'assets/icons/web-guest/expired.svg';
import dayjs from 'dayjs';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import EIQPreviewImage from 'common/components/image/eiq-preview-image';
import { SecurityTypeEnum } from 'common/enums/security-type.enum';
import { ResidentTypesEnum } from 'common/enums/resident-types.enum';

const getVisitorTypeTag = (trafficLog: ITrafficRead) => {
  if (!trafficLog?.visitorType) {
    return <></>;
  }

  const visitorType = trafficLog.visitorType as TrafficLogVisitorType;

  if (visitorType !== TrafficLogVisitorType.Resident || !trafficLog.residentType) {
    return (
      <Tag className="eiq-tag" color={visitorTypeMapColorMap[visitorType]} style={{ color: '#292B2E' }}>
        {visitorTypeMap[visitorType]}
      </Tag>
    );
  }

  const residentTyep = trafficLog.residentType as ResidentTypesEnum;
  const relatedToResidentType = trafficLog.relatedToResidentType as ResidentTypesEnum;

  return (
    <Tag className="eiq-tag" color={ownershipTypeStatusColorMap[getOwnerShipTypeToMap(residentTyep, relatedToResidentType)!]}>
      {trafficLogOwnershipTypeStatusMap[residentTyep]}
    </Tag>
  );
};

export const trafficColumns = () => ({
  entered: {
    key: 'timestamp',
    title: 'Entered',
    dataKey: 'timestamp',
    width: 150,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      return <BaseCellText>{formatCustomDateTime(cell?.rowData?.timestamp, true)}</BaseCellText>;
    },
  },
  direction: {
    key: 'direction',
    title: 'Direction',
    dataKey: 'direction',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      const isEntry = cell?.cellData === 1;
      return (
        <Tag className="eiq-tag" color={isEntry ? '#C1FFEA' : '#DCDCDC'} style={{ color: '#292B2E' }}>
          {isEntry ? 'Entry' : 'Exit'}
        </Tag>
      );
    },
  },
  address: {
    key: 'address',
    title: 'Address',
    dataKey: 'address',
    width: 136,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  residentName: {
    key: 'residentName',
    title: 'Resident’s name',
    dataKey: 'residentName',
    width: 200,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  guestName: {
    key: 'guestName',
    title: 'Guest’s name',
    dataKey: 'guestName',
    width: 170,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  guestType: {
    key: 'visitorType',
    title: 'Type',
    dataKey: 'visitorType',
    width: 200,
    minWidth: 160,
    resizable: true,
    sortable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      return getVisitorTypeTag(cell.rowData);
    },
  },
  entryType: {
    key: 'entryType',
    title: 'Entry type',
    dataKey: 'entryType',
    width: 200,
    minWidth: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      return (
        <Tag className="eiq-tag" color={entryTypeColorMap[cell?.rowData?.entryType as EntryType]}>
          {entryTypeMap[cell?.rowData?.entryType as EntryType]}
        </Tag>
      );
    },
  },
  gate: {
    key: 'gate',
    title: 'Location',
    dataKey: 'gate',
    width: 130,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      const gateName = cell.cellData;
      const isVirtual = cell.rowData?.securityType?.toLocaleLowerCase() === SecurityTypeEnum.Virtual?.toLocaleLowerCase();
      return (
        <BaseCellText>
          {gateName}
          {isVirtual ? ' - Virtual' : ''}
        </BaseCellText>
      );
    },
  },
  camera: {
    key: 'camera',
    title: 'Camera',
    dataKey: 'camera',
    width: 130,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  carState: {
    key: 'carState',
    title: 'State',
    dataKey: 'carState',
    width: 105,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  carLicensePlate: {
    key: 'carLicensePlate',
    title: 'License Plate',
    dataKey: 'carLicensePlate',
    width: 220,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  smartPlateId: {
    key: 'smartPlateId',
    title: 'SmartPlate ID',
    dataKey: 'smartPlateId',
    width: 150,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  deviceId: {
    key: 'deviceId',
    title: 'Device ID',
    dataKey: 'deviceId',
    width: 150,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  admittance: {
    key: 'admittanceTypes',
    title: 'Admittance',
    dataKey: 'admittanceTypes',
    width: 155,
    sortable: false,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      return (
        <>
          {cell?.cellData?.map((item: ITrafficAdmittanceType, index: number) => {
            return (
              <Tag
                key={`admittance-type-${index}`}
                className={`eiq-tag ${admittanceTagClassMap[item.admittanceType as AdmittanceType]}`}
                color={admittanceColorMap[item.admittanceType as AdmittanceType]}>
                {admittanceTypeMap[item.admittanceType as AdmittanceType]}
              </Tag>
            );
          })}
        </>
      );
    },
  },
  turnBackReason: {
    key: 'turnBackReason',
    title: 'Reason',
    dataKey: 'turnBackReason',
    width: 180,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  image: {
    key: 'imageUrl',
    title: 'PlateScan Image',
    dataKey: 'imageUrl',
    width: 175,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: any) => {
      if (!cell.cellData) {
        return '';
      }

      return (
        <div style={{ margin: 'auto' }}>
          <EIQPreviewImage url={cell.cellData} fileType={UploadFileTypes.PlateRecognizer} alt="License plate image" isPublic />
        </div>
      );
    },
  },
  companyName: {
    key: 'companyName',
    title: 'Company name',
    dataKey: 'companyName',
    width: 185,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  carExpirationDate: {
    key: 'carExpirationDate',
    title: 'Device Expiration Date',
    dataKey: 'carExpirationDate',
    width: 250,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      if (!cell.cellData) {
        return;
      }

      const expirationDate = dayjs(cell.cellData, 'YYYY-MM-DD').startOf('day');
      const dayToCompare = dayjs(cell.rowData.timestamp, 'YYYY-MM-DD').startOf('day');
      const isExpired = expirationDate.isBefore(dayToCompare);

      return isExpired ? (
        <Tag className="eiq-tag expired" icon={<Expired />}>
          {formatCustomDateTime(cell.cellData)}
        </Tag>
      ) : (
        `${formatCustomDateTime(cell.cellData)}`
      );
    },
  },
  driverName: {
    key: 'driverName',
    title: 'Driver’s name',
    dataKey: 'driverName',
    width: 185,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  driverCompanyName: {
    key: 'driverCompanyName',
    title: 'Driver’s company name',
    dataKey: 'driverCompanyName',
    width: 225,
    sortable: true,
    resizable: true,
    fixed: 1,
  },
  note: {
    key: 'note',
    title: 'Note',
    dataKey: 'note',
    width: 275,
    sortable: false,
    resizable: true,
    fixed: 1,
  },
  EIQPassport: {
    key: 'isEIQPassportUsed',
    title: 'QR Passport',
    dataKey: 'isEIQPassportUsed',
    width: 140,
    sortable: true,
    resizable: true,
    fixed: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      return (
        <Tag className="eiq-tag" color={!cell.cellData ? '#FFB68C' : '#C1FFEA'} style={{ color: '#292B2E' }}>
          {!cell.cellData ? 'No' : 'Yes'}
        </Tag>
      );
    },
  },
  idDocumentImage: {
    key: 'idDocument',
    title: 'Photo ID',
    dataKey: 'idDocument',
    width: 120,
    minWidth: 120,
    resizable: true,
    sortable: false,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ITrafficRead>) => {
      const imageUrl = cell?.rowData?.idDocumentUrl;
      if (!imageUrl) {
        return '';
      }

      const isDocument = !!cell?.rowData?.idDocumentType && cell?.rowData?.idDocumentType !== 'snapshot';

      return (
        <div style={{ margin: 'auto' }}>
          <EIQPreviewImage
            isDocument={isDocument}
            width={isDocument ? 28 : 20}
            url={imageUrl}
            fileType={UploadFileTypes.IdDocument}
            alt="Id document image"
          />
        </div>
      );
    },
  },
  verifiedName: {
    key: 'verifiedName',
    title: 'Verified ID Name',
    dataKey: 'verifiedName',
    width: 250,
    sortable: false,
    resizable: true,
    fixed: 1,
  },
  owner: {
    key: 'owner',
    title: 'Owner',
    dataKey: 'owner',
    width: 250,
    sortable: false,
    resizable: true,
    fixed: 1,
  },
});

const {
  gate,
  camera,
  carState,
  carLicensePlate,
  smartPlateId,
  deviceId,
  direction,
  guestName,
  residentName,
  admittance,
  entered,
  image,
  turnBackReason,
  address,
  guestType,
  entryType,
  companyName,
  carExpirationDate,
  driverName,
  driverCompanyName,
  note,
  EIQPassport,
  idDocumentImage,
  owner,
  verifiedName,
} = trafficColumns();

export const trafficInitialColumns = [
  entered,
  direction,
  gate,
  camera,
  address,
  residentName,
  guestName,
  companyName,
  entryType,
  guestType,
  carState,
  carLicensePlate,
  smartPlateId,
  deviceId,
  carExpirationDate,
  admittance,
  turnBackReason,
  driverName,
  driverCompanyName,
  note,
  image,
  EIQPassport,
  idDocumentImage,
  owner,
  verifiedName,
];

const getOwnerShipTypeToMap = (ownerShiptype?: ResidentTypesEnum | null, realtedToOwnerShiptype?: ResidentTypesEnum | null) => {
  if (ownerShiptype === ResidentTypesEnum.Renter || realtedToOwnerShiptype === ResidentTypesEnum.Renter) {
    return ResidentTypesEnum.Renter;
  }
  return ownerShiptype;
};
